import React, { useEffect, useState, useCallback } from 'react';
import { useDebounce } from '../../../Utils/Debounce';
import { useSelector } from 'react-redux';
import PageTitle from '../../../Utils/PageTitle';
import { FacilityCardsContainer, PaginationContainer } from '../Facility/styledComponents';
import { ContainerRow, TitleRow, SearchInputStyle } from '../styledComponents';
import { StyledPagination } from '../Hl7/styledComponents';
import CustomTypography from '../../CustomComponents/CustomTypography';
import { getFacilites, getFacilityTemplatesByGod } from '../../../Apis/Facility';
import { notification } from 'antd';
import DictionaryFacilityCard from './DictionaryFacilityCard';
import FacilityReportExportModal from './FacilityReportExportModal';
import { PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import { AddButton } from '../Dictation/styledComponents';
import { useHistory } from 'react-router-dom';
import Spinner from '../../CustomComponents/Spinner';
import { SearchOutlined } from '@ant-design/icons';
import { SEARCH_DELAY } from '../../../Constants/StringConstants';
import { Button, Tooltip } from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  DoubleLeftOutlined,
  DoubleRightOutlined
} from '@ant-design/icons';

const DictionaryFacility = () => {
  const [theme] = useSelector(state => [state.themeReducer]);
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [facilities, setFacilities] = useState([]);
  const [searchString, setSearchString] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(12);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const user = JSON.parse(localStorage.getItem('user'));

  const fetchAll = (pageNo, size = pageSize) => {
    setLoading(true);
    // Use appropriate API based on user type
    const apiCall = user?.userType === 'cascadeGod'
      ? getFacilityTemplatesByGod(pageNo, searchString, size)
      : getFacilites(pageNo, searchString, size);

    apiCall
      .then(response => {
        setLoading(false);
        if (response.data.success) {
          setTotal(response.data.size);

          // Transform God user response to match expected structure
          if (user?.userType === 'cascadeGod') {
            const transformedFacilities = response.data.result.map(facility => ({
              facilityId: facility.id,
              facilityName: facility.facilityName,
              disabled: false,
              originalData: facility, // Store original data for editing
              fields: [
                {
                  fieldName: 'EHR Type',
                  fieldValue: facility.ehrConfigForFacilityTemplate?.ehrType || ''
                },
                {
                  fieldName: 'Organization ID',
                  fieldValue: facility.ehrConfigForFacilityTemplate?.organisationId || ''
                },
                {
                  fieldName: 'Facility ID',
                  fieldValue: facility.ehrConfigForFacilityTemplate?.facilityId || ''
                }
              ]
            }));
            setFacilities(transformedFacilities);
          } else {
            setFacilities(response.data.result);
          }
        } else {
          notification['error']({
            message: 'Failed',
            description: response.data.message
          });
        }
      })
      .catch(err => {
        console.log(err);
        setLoading(false);
      });
  };

  const onChangeHandler = value => {
    setCurrentPage(0);
    setSearchString(value);
  };

  const debounceOnChange = useCallback(
    useDebounce(onChangeHandler, SEARCH_DELAY),
    []
  );

  const pageNoChangeHandler = (pageNo, newPageSize) => {
    fetchAll(pageNo === 0 ? 0 : pageNo - 1, newPageSize);
  };

  const pageSizeChangeHandler = (current, size) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const itemRender = (current, type, originalElement) => {
    if (type === 'prev') {
      return (
        <>
          <Tooltip title='First Page'>
            <Button
              type='text'
              icon={<DoubleLeftOutlined />}
              onClick={e => {
                e.stopPropagation();
                setCurrentPage(0);
                pageNoChangeHandler(0);
              }}
            />
          </Tooltip>
          <Tooltip title='Previous Page'>
            <Button type='text' icon={<LeftOutlined />} />
          </Tooltip>
        </>
      );
    }
    if (type === 'next') {
      return (
        <>
          <Tooltip title='Next Page'>
            <Button type='text' icon={<RightOutlined />} />
          </Tooltip>
          <Tooltip title='Last Page'>
            <Button
              type='text'
              icon={<DoubleRightOutlined />}
              onClick={e => {
                e.stopPropagation();
                setCurrentPage(Math.floor(total / pageSize + 1));
                pageNoChangeHandler(Math.floor(total / pageSize + 1));
              }}
            />
          </Tooltip>
        </>
      );
    }
    return originalElement;
  };

  useEffect(() => {
    fetchAll(0);
    // eslint-disable-next-line
  }, [searchString]);

  return (
    <>
      <PageTitle title='Facility' />
      <ContainerRow gutter={[0, 40]}>
        <TitleRow justify='space-between'>
          <CustomTypography color={theme['@text-color']}>
            Facility
          </CustomTypography>
          <div>
            {user?.userType === 'cascadeGod' && (
              <AddButton
                icon={<DownloadOutlined />}
                type='primary'
                shape='round'
                onClick={() => setExportModalOpen(true)}
              >
                Export
              </AddButton>
            )}
            <AddButton
              icon={<PlusOutlined />}
              type='primary'
              shape='round'
              onClick={() => history.push('/dashboard/facility-management/new')}
            >
              Add
            </AddButton>
            <SearchInputStyle
              placeholder='Search'
              prefix={<SearchOutlined />}
              onChange={event => debounceOnChange(event.target.value)}
            />
          </div>
        </TitleRow>
        {loading ? (
          <Spinner />
        ) : facilities.length > 0 ? (
          <FacilityCardsContainer>
            {facilities.map(facility => (
              <DictionaryFacilityCard facility={facility} key={facility.facilityId} />
            ))}
          </FacilityCardsContainer>
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '250px'
            }}
          >
            {' '}
            <h1>No facilities added</h1>
          </div>
        )}

        <PaginationContainer>
          <StyledPagination
            setPageNo={current => {
              setCurrentPage(current);
            }}
            pageNo={currentPage}
            onChange={pageNoChangeHandler}
            pageSize={pageSize}
            total={total}
            itemRender={itemRender}
            onShowSizeChange={pageSizeChangeHandler}
          />
        </PaginationContainer>
      </ContainerRow>
      {user?.userType === 'cascadeGod' && (
        <FacilityReportExportModal
          visible={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
        />
      )}
    </>
  );
};

export default DictionaryFacility;
